// src/pages/SignupPage.jsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, firestore } from "../firebase";

export default function SignupPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function createUserProfile(user) {
    const userRef = doc(firestore, "users", user.uid);

    await setDoc(
      userRef,
      {
        uid: user.uid,
        email: user.email || "",
        displayName: user.displayName || "",
        avatar: user.photoURL || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  async function handleEmailSignup(e) {
    e.preventDefault();

    if (!email.trim() || !password.trim()) {
      setError("Please enter an email and password.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const result = await createUserWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );

      await createUserProfile(result.user);

      navigate("/");
    } catch (err) {
      console.error("Signup failed:", err);
      setError(getFriendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignup() {
    try {
      setLoading(true);
      setError("");

      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      await createUserProfile(result.user);

      navigate("/");
    } catch (err) {
      console.error("Google signup failed:", err);
      setError(getFriendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <p style={eyebrowStyle}>Antarctic Press Portal</p>

        <h1 style={titleStyle}>Create your account</h1>

        <p style={subtitleStyle}>
          Sign up to pick up where you left off, follow campaigns, and unlock a
          personalized Antarctic Press experience.
        </p>

        {error ? <div style={errorStyle}>{error}</div> : null}

        <form onSubmit={handleEmailSignup} style={formStyle}>
          <label style={labelStyle}>
            Email
            <input
              type="email"
              value={email}
              autoComplete="email"
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              disabled={loading}
            />
          </label>

          <label style={labelStyle}>
            Password
            <input
              type="password"
              value={password}
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              disabled={loading}
            />
          </label>

          <label style={labelStyle}>
            Confirm Password
            <input
              type="password"
              value={confirmPassword}
              autoComplete="new-password"
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={inputStyle}
              disabled={loading}
            />
          </label>

          <button type="submit" style={primaryButtonStyle} disabled={loading}>
            {loading ? "Creating Account..." : "Create Account"}
          </button>
        </form>

        <div style={dividerStyle}>
          <span style={dividerLineStyle} />
          <span style={dividerTextStyle}>or</span>
          <span style={dividerLineStyle} />
        </div>

        <button
          type="button"
          onClick={handleGoogleSignup}
          style={googleButtonStyle}
          disabled={loading}
        >
          Continue with Google
        </button>

        <p style={footerTextStyle}>
          Already have an account?{" "}
          <Link to="/portal/login" style={linkStyle}>
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}

function getFriendlyAuthError(err) {
  switch (err?.code) {
    case "auth/email-already-in-use":
      return "That email address is already in use.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/weak-password":
      return "Password should be at least 6 characters.";
    case "auth/popup-closed-by-user":
      return "The Google sign-up window was closed before completing.";
    default:
      return "Something went wrong while creating your account.";
  }
}

const pageStyle = {
  minHeight: "calc(100vh - 80px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "3rem 1rem",
  background: "#f3f6fa",
};

const cardStyle = {
  width: "100%",
  maxWidth: "460px",
  background: "#fff",
  border: "1px solid #d7e2ef",
  borderTop: "5px solid #114c96",
  borderRadius: "14px",
  padding: "2rem",
  boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
};

const eyebrowStyle = {
  margin: "0 0 0.5rem 0",
  color: "#114c96",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  fontSize: "0.78rem",
  fontWeight: 800,
};

const titleStyle = {
  margin: "0 0 0.75rem 0",
  color: "#111827",
  fontSize: "2rem",
  fontWeight: 900,
};

const subtitleStyle = {
  margin: "0 0 1.5rem 0",
  color: "#4b5563",
  lineHeight: 1.6,
};

const formStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
};

const labelStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "0.4rem",
  color: "#111827",
  fontWeight: 700,
};

const inputStyle = {
  padding: "0.8rem 0.9rem",
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  fontSize: "1rem",
};

const primaryButtonStyle = {
  marginTop: "0.5rem",
  background: "#114c96",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "0.85rem 1rem",
  fontWeight: 800,
  cursor: "pointer",
};

const googleButtonStyle = {
  width: "100%",
  background: "#fff",
  color: "#111827",
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  padding: "0.85rem 1rem",
  fontWeight: 800,
  cursor: "pointer",
};

const dividerStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  margin: "1.5rem 0",
};

const dividerLineStyle = {
  height: "1px",
  background: "#d7e2ef",
  flex: 1,
};

const dividerTextStyle = {
  color: "#64748b",
  fontWeight: 700,
};

const errorStyle = {
  background: "#fff1f2",
  color: "#be123c",
  border: "1px solid #fecdd3",
  borderRadius: "8px",
  padding: "0.85rem",
  marginBottom: "1rem",
  fontWeight: 700,
};

const footerTextStyle = {
  margin: "1.5rem 0 0 0",
  color: "#4b5563",
  textAlign: "center",
};

const linkStyle = {
  color: "#114c96",
  fontWeight: 800,
};